
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import CategorySection from '../components/CategorySection';
import MedicationsHero from '../components/MedicationsHero';
import { useAuth } from '@/contexts/AuthContext';
import { medications } from '@/data/medications';
import MedicationsNavbar from '@/components/medications/MedicationsNavbar';
import NoResults from '@/components/medications/NoResults';

const Medications = () => {
  const { isAuthenticated, isProviderView, toggleProviderView, logout } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  
  useEffect(() => {
    console.log("Medications page loaded", { 
      isAuthenticated, 
      isProviderView 
    });
  }, [isAuthenticated, isProviderView]);

  const scrollToCategory = (categoryId: string) => {
    const element = document.getElementById(categoryId);
    if (element) {
      // Add an offset to account for the navbar height and sticky tabs
      const navbarHeight = 64; // This is the h-16 class in pixels
      const tabsHeight = 56; // Height of tabs (approximate)
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - navbarHeight - tabsHeight;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Filter medications based on search term
  const filteredMedications = medications.filter(med => {
    if (!searchTerm.trim()) return true;
    
    const searchTermLower = searchTerm.toLowerCase();
    return (
      med.name.toLowerCase().includes(searchTermLower) ||
      med.description.toLowerCase().includes(searchTermLower) ||
      med.research.toLowerCase().includes(searchTermLower)
    );
  });

  // Count medications in each category after filtering
  const categoryCounts = {
    'weight-loss': filteredMedications.filter(med => med.category === 'weight-loss').length,
    'mens-health': filteredMedications.filter(med => med.category === 'mens-health').length,
    'womens-health': filteredMedications.filter(med => med.category === 'womens-health').length,
    'peptides': filteredMedications.filter(med => med.category === 'peptides').length,
    'injectables': filteredMedications.filter(med => med.category === 'injectables').length,
    'additional': filteredMedications.filter(med => med.category === 'additional').length,
  };

  const clearSearch = () => setSearchTerm('');
  
  const hasNoResults = Object.values(categoryCounts).every(count => count === 0);

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Medications - AutoPilot Care</title>
        <meta name="description" content="Browse our comprehensive collection of medication categories and research information." />
        <meta property="og:title" content="Medications - AutoPilot Care" />
        <meta property="og:description" content="Browse our comprehensive collection of medication categories and research information." />
        <meta property="og:image" content="/images/autopilot-og-image.png?v=6" />
        <meta property="og:updated_time" content="2024-04-11T00:00:00Z" />
        <meta property="og:url" content="https://autopilotcare.com/meds?v=6" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
        <link rel="preconnect" href="https://www.youtube.com" />
        <link rel="preconnect" href="https://www.youtube-nocookie.com" />
      </Helmet>
      <Navbar />
      
      <MedicationsHero />
      
      <MedicationsNavbar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        isAuthenticated={isAuthenticated}
        isProviderView={isProviderView}
        toggleProviderView={toggleProviderView}
        logout={logout}
        scrollToCategory={scrollToCategory}
        categoryCounts={categoryCounts}
      />
      
      <main className="flex-grow pt-4">
        {categoryCounts['weight-loss'] > 0 && (
          <CategorySection 
            category="weight-loss" 
            filteredMedications={filteredMedications.filter(med => med.category === 'weight-loss')} 
          />
        )}
        {categoryCounts['mens-health'] > 0 && (
          <CategorySection 
            category="mens-health" 
            filteredMedications={filteredMedications.filter(med => med.category === 'mens-health')} 
          />
        )}
        {categoryCounts['womens-health'] > 0 && (
          <CategorySection 
            category="womens-health" 
            filteredMedications={filteredMedications.filter(med => med.category === 'womens-health')} 
          />
        )}
        {categoryCounts['peptides'] > 0 && (
          <CategorySection 
            category="peptides" 
            filteredMedications={filteredMedications.filter(med => med.category === 'peptides')} 
          />
        )}
        {categoryCounts['injectables'] > 0 && (
          <CategorySection 
            category="injectables" 
            filteredMedications={filteredMedications.filter(med => med.category === 'injectables')} 
          />
        )}
        {categoryCounts['additional'] > 0 && (
          <CategorySection 
            category="additional" 
            filteredMedications={filteredMedications.filter(med => med.category === 'additional')} 
          />
        )}
        
        {hasNoResults && <NoResults clearSearch={clearSearch} />}
      </main>
      
      <Footer />
    </div>
  );
};

export default Medications;
